import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { fmtDate, EVENT_STATUS_LABEL, usePageMeta } from "@/lib/content";
import { Reveal } from "@/components/site/Reveal";
import EmbedConsent from "@/components/site/EmbedConsent";

export default function ReleaseDetail() {
  const { slug } = useParams();
  const { data: r, isLoading, isError } = useQuery({
    queryKey: ["release", slug],
    queryFn: async () => (await api.get(`/public/releases/slug/${slug}`)).data,
    retry: false,
  });

  usePageMeta(
    r ? `SEIMEN DEXTER — ${r.title}` : "SEIMEN DEXTER — Music",
    r
      ? `${r.title} by ${r.artist}${r.label ? `, released on ${r.label}` : ""}. Hard / Industrial Techno — stream and buy.`
      : "Releases by SEIMEN DEXTER."
  );

  useEffect(() => {
    if (!r) return undefined;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "MusicRecording",
      name: r.title,
      byArtist: { "@type": "MusicGroup", name: r.artist },
      datePublished: r.release_date,
      inAlbum: r.label ? { "@type": "MusicRelease", name: r.title, recordLabel: r.label } : undefined,
      url: r.spotify_url || r.soundcloud_url || window.location.href,
      image: r.cover_url ? new URL(fileUrl(r.cover_url), window.location.origin).href : undefined,
    });
    document.head.appendChild(el);
    return () => el.remove();
  }, [r]);

  if (isLoading) {
    return <div className="px-5 md:px-10 pt-40 pb-24 text-white/40 text-sm">LOADING…</div>;
  }

  if (isError || !r) {
    return (
      <div className="px-5 md:px-10 pt-40 pb-24">
        <p className="font-display text-6xl">RELEASE NOT FOUND.</p>
        <Link data-testid="release-not-found-back" to="/music" className="btn-ghost mt-10">
          ALL MUSIC
        </Link>
      </div>
    );
  }

  const d = fmtDate(r.release_date);
  const links = [
    { label: "LISTEN", url: r.spotify_url, testid: "release-listen" },
    { label: "STREAM", url: r.soundcloud_url, testid: "release-stream" },
    { label: "BUY", url: r.beatport_url, testid: "release-buy" },
    { label: "APPLE MUSIC", url: r.apple_url, testid: "release-apple" },
    { label: "MORE", url: r.other_url, testid: "release-other" },
  ].filter((l) => l.url);

  return (
    <div className="pt-24 md:pt-32 pb-24 md:pb-36">
      <div className="px-5 md:px-10 pt-6 md:pt-10">
        <Link data-testid="release-back" to="/music" className="inline-flex items-center gap-3 text-[11px] tracking-[0.3em] text-white/50 hover:text-white transition-colors">
          <ArrowLeft className="h-4 w-4" /> ALL MUSIC
        </Link>
      </div>
      <div className="px-5 md:px-10 mt-10 md:mt-16 grid gap-12 md:grid-cols-12 items-start">
        <Reveal className="md:col-span-5" y={40}>
          {r.cover_url ? (
            <img
              src={fileUrl(r.cover_url)}
              alt={`${r.artist} — ${r.title} cover artwork`}
              className="w-full aspect-square object-cover border border-line"
              draggable="false"
            />
          ) : (
            <div className="w-full aspect-square border border-line bg-panel flex items-center justify-center">
              <span className="kicker">NO COVER</span>
            </div>
          )}
        </Reveal>
        <div className="md:col-span-7">
          <p className="kicker mb-6">
            {r.effective_status === "upcoming" ? `UPCOMING — ${d.full}` : `OUT NOW — ${d.full}`}
          </p>
          <h1 className="font-display text-6xl md:text-8xl leading-[0.92]">{r.title}</h1>
          <p className="mt-6 text-white/70 text-sm md:text-base">{r.artist}</p>
          {(r.label || r.catalog_number) && (
            <dl className="mt-10 border-t border-line text-sm">
              {r.label && (
                <div className="flex justify-between py-3 border-b border-line">
                  <dt className="text-white/40 tracking-[0.2em] text-[11px] pt-1">LABEL</dt>
                  <dd>{r.label}</dd>
                </div>
              )}
              {r.catalog_number && (
                <div className="flex justify-between py-3 border-b border-line">
                  <dt className="text-white/40 tracking-[0.2em] text-[11px] pt-1">CAT No.</dt>
                  <dd>{r.catalog_number}</dd>
                </div>
              )}
              <div className="flex justify-between py-3 border-b border-line">
                <dt className="text-white/40 tracking-[0.2em] text-[11px] pt-1">STATUS</dt>
                <dd>{EVENT_STATUS_LABEL[r.effective_status] || "OUT NOW"}</dd>
              </div>
            </dl>
          )}
          <div className="mt-10 flex flex-wrap gap-3 md:gap-4">
            {links.map((l) => (
              <a key={l.label} data-testid={l.testid} href={l.url} target="_blank" rel="noreferrer" className="btn-primary">
                {l.label}
              </a>
            ))}
          </div>
        </div>
      </div>
      {r.soundcloud_url && (
        <div className="px-5 md:px-10 mt-16 md:mt-24">
          <p className="kicker mb-6">PREVIEW</p>
          <EmbedConsent type="soundcloud" url={r.soundcloud_url} title={`${r.title} — SoundCloud player`} aspect="h-[220px]" />
        </div>
      )}
    </div>
  );
}
